import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import * as logger from 'loglevel'
import {
  applyChildAppDocking,
  childAppsRequestDocking,
  listDockAnchors,
} from '../../Utils/childAppDocking'
import { DefaultContent } from './constants'
import {
  ensureChildAppsContainer,
  initializeTrackedChildApps,
  syncChildApps,
} from './childAppsDom'

export function useChildApps({ frameContentRef, html, anearEvent, anearParticipant, userAuth }) {
  const childAppsContainerRef = useRef(null)
  // Key: appName, Value: Set of active <anear-app> elements for that app
  const activeChildAppsRef = useRef(new Map())

  const getChildAppsContainer = useCallback(() => {
    return ensureChildAppsContainer(childAppsContainerRef, frameContentRef, anearEvent.id)
  }, [anearEvent, frameContentRef])

  // Move <anear-app> elements from parent HTML to the overlay before paint
  // so a parent HTML replacement does not destroy the child shadow DOM.
  useLayoutEffect(() => {
    const container = frameContentRef.current
    if (!container || !html || html === DefaultContent) return

    const childAppsContainer = getChildAppsContainer()
    if (!childAppsContainer) {
      logger.warn('EventFrame: Child apps container not available in useLayoutEffect')
      return
    }

    syncChildApps(container, childAppsContainer, activeChildAppsRef.current)
  }, [html, getChildAppsContainer, frameContentRef])

  // Opt-in docking for a child app included in this display.
  // The <anear-app> names an anchor and the custom property its CSS reads.
  useEffect(() => {
    if (!html || html === DefaultContent) return undefined

    const frameEl = frameContentRef.current
    const childAppsContainer = getChildAppsContainer()
    if (!frameEl || !childAppsContainer) return undefined
    if (!childAppsRequestDocking(childAppsContainer)) return undefined

    let rafId = 0
    let ro = null

    const schedule = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => applyChildAppDocking(frameEl, childAppsContainer))
    }

    schedule()

    try {
      window.addEventListener('resize', schedule)
      frameEl.addEventListener('scroll', schedule, { passive: true })

      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(schedule)
        ro.observe(childAppsContainer)
        listDockAnchors(frameEl).forEach((anchor) => ro.observe(anchor))
      }
    } catch (_e) {
      // ignore
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      try {
        window.removeEventListener('resize', schedule)
        frameEl.removeEventListener('scroll', schedule)
      } catch (_e) {
        // ignore
      }
      ro?.disconnect?.()
    }
  }, [html, getChildAppsContainer, frameContentRef])

  // Wait until the parent participant relationship exists, then initialize
  // each tracked <anear-app>. The short delay runs after the layout effect
  // has moved hosts into the overlay.
  useEffect(() => {
    if (!html || !anearEvent || html === DefaultContent) return undefined

    const isParentParticipantEstablished = anearParticipant !== null ||
                                          anearEvent.isCreator()

    if (!isParentParticipantEstablished) {
      logger.debug('EventFrame: Waiting for parent participant to be established before initializing child apps')
      return undefined
    }

    if (!frameContentRef.current) return undefined

    const timeoutId = setTimeout(() => {
      initializeTrackedChildApps(
        getChildAppsContainer(),
        activeChildAppsRef.current,
        anearEvent,
        anearParticipant,
        userAuth
      )
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [html, anearEvent, anearParticipant, userAuth, getChildAppsContainer, frameContentRef])

  return { childAppsContainerRef, activeChildAppsRef }
}
